import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { Router } from '@angular/router';
import { Observable, of } from 'rxjs';
import { map, startWith } from 'rxjs/operators';
import { Module } from 'src/app/shared/interfaces/module';
import { MenuService } from 'src/app/shared/service/menu/menu.service';

@Component({
  selector: 'app-load-sidebar-modules',
  templateUrl: './load-sidebar-modules.component.html',
  styleUrls: ['./load-sidebar-modules.component.scss']
})
export class LoadSidebarModulesComponent implements OnInit {
  public form: FormGroup;

  modules: Module[] = [];
  filteredModules: Observable<Module[]> = of([]);

  get searchControl() {
    return this.form.controls['search']
  };

  constructor(
    private fb: FormBuilder,
    private menuService: MenuService,
    private router: Router
  ) {
    this.form = this.fb.group({
      search: ['']
    });
    this.load();
  }

  ngOnInit(): void {
    this.filteredModules = this.searchControl.valueChanges.pipe(
      startWith(''),
      map((value) => (value === null ? [] : this._recursiveFilter(value)))
    );
  }

  private _recursiveFilter(value: string | Module): Module[] {
    if (typeof value === 'object') return [value];
    const filteredValue = value.toLowerCase();
    if (!filteredValue) return [];

    const result: Module[] = [];
    this._recursiveSearch(filteredValue, this.modules, result);

    return result;
  }

  private _recursiveSearch = (value: string, modules: Module[], output: Module[]): Module[] => {
    return modules.reduce((acc, module) => {
      if (module.Nombre.toLowerCase().includes(value) &&
        module.type === 'link') {
        acc.push(module);
      }
      if (module?.children?.length) {
        acc = this._recursiveSearch(value, module.children, acc);
      }

      return acc;
    }, output);
  };

  load() {
    this.menuService.getMenu().subscribe({
      next: resp => {
        this.modules = resp.data;
        this.modules.forEach((children: any) => {
          if (children.children.length > 0) {
            children.children.forEach((subchildren: any) => {
              subchildren.type = subchildren.type.type
              if (subchildren.subchild.length === 0) {
                delete subchildren.subchild;
              }
            });
          }
          children.type = children.type.type
        });
        this.assignLevels(this.modules);
        this.assignUrls(this.modules);
      },
      error: error => {
        console.log(error);
      }
    })
  }

  displayFn(module: Module): string {
    return module && module.Estado ? module.Estado : '';
  }

  private assignLevels(modules: Module[], level: number = 1) {
    for (const module of modules) {
      (module as { [key: string]: any })['level'] = level;
      if (module?.children && module?.children.length > 0) {
        this.assignLevels(module?.children, level + 1);
      }
    }
  }

  private assignUrls(modules: Module[], parentUrl: string = '') {
    for (const module of modules) {
      // Solo agrega Estado si existe
      const currentUrl = module.Estado ? `${parentUrl}/${module.Estado}` : parentUrl;
      (module as { [key: string]: any })['url'] = currentUrl;
      if (module?.children && module.children.length > 0) {
        this.assignUrls(module.children, currentUrl);
      }
    }
  }

  onOptionSelected(module: MatAutocompleteSelectedEvent) {
    const selectedModule: Module = module.option.value;
    this.navigateToModules(selectedModule);
  }

  navigateToModules(module: Module) {
    if (!module.url) return;
    this.router.navigateByUrl(`/${module.url}`)
      .finally(() => this.searchControl.reset())
      .catch((err) => console.error(err));
  }

  getPanelClass(module: Module): string {
    return module.Estado ? `panel-level-${module.level} navigate` : `panel-level-${module.level}`;
  }

  navigateWithParams(link: string, params: Map<string, string | number>) {
    this.router
      .navigate([link], {
        queryParams: params,
      })
      .finally(() => this.searchControl.reset())
      .catch((err) => console.error(err));
  }
}
